import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AnalyticsCharts = ({ reports }) => {
  // Prepare data for status distribution pie chart
  const statusData = [
    { name: 'New', value: reports.filter(r => r.status === 'New').length, color: '#3B82F6' },
    { name: 'Under Review', value: reports.filter(r => r.status === 'Under Review').length, color: '#F59E0B' },
    { name: 'Resolved', value: reports.filter(r => r.status === 'Resolved').length, color: '#10B981' },
    { name: 'Rejected', value: reports.filter(r => r.status === 'Rejected').length, color: '#EF4444' },
    { name: 'On Hold', value: reports.filter(r => r.status === 'On Hold').length, color: '#6B7280' }
  ].filter(item => item.value > 0);

  // Prepare data for monthly submissions bar chart
  const getMonthlyData = () => {
    const monthlyStats = {};
    const currentDate = new Date();

    // Get last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      monthlyStats[monthKey] = 0;
    }

    // Count reports by month
    reports.forEach(report => {
      const reportDate = new Date(report.created_at);
      const monthKey = reportDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (monthlyStats.hasOwnProperty(monthKey)) {
        monthlyStats[monthKey]++;
      }
    });

    return Object.entries(monthlyStats).map(([month, count]) => ({
      month,
      reports: count
    }));
  };

  const monthlyData = getMonthlyData();

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Status Distribution Pie Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Distribution</h3>
        {statusData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            No data available
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 justify-center">
          {statusData.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              ></div>
              <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Submissions Bar Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Report Submissions</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip
              formatter={(value) => [value, 'Reports']}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Bar dataKey="reports" fill="#3B82F6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
